import type { TippyProps } from '@tippyjs/react';
import type { ReactNode, ReactElement } from 'react';

import Tippy from '@tippyjs/react';
import { useMemo } from 'react';

import classy from '@core/utils/classy';
import Flex from '@ui/Flex';
import KbdShortcut from '@ui/KbdShortcut';

import './style.scss';

export interface TooltipProps extends Omit<TippyProps, 'singleton'> {
  /**
   * Tooltip designed to replace HTML [title]
   */
  asTitle?: boolean;

  /**
   * Tooltip content to show when reference element is triggered.
   */
  content: ReactNode;

  /**
   * Optional keyboard shortcut shown after `content`, with each entry in its own `<kbd>`.
   * Example: `shortcutKeys={['CTRL', 'J']}` → CTRL and J as styled keys.
   */
  shortcutKeys?: string[];

  /**
   * When a `string`, references the TooltipSingleton by `id` as its singleton
   * source. Otherwise, expects a SingletonObject `source` or `target` returned
   * from [`useSingleton`](https://github.com/atomiks/tippyjs-react#-usesingleton).
   */
  singleton?: TippyProps['singleton'] | string;
}

/**
 * Trigger Tippy on any element by wrapping it in this tooltip component. You
 * can pass plain-text or rich JSX to the tooltip using the `content` prop.
 */
const Tooltip = ({ children, content, className, singleton, asTitle, shortcutKeys, ...props }: TooltipProps) => {
  const classes = useMemo(() => classy('rm-Tooltip', className), [className]);
  const trigger = typeof children === 'string' ? <span className="tippy-text">{children}</span> : children;

  interface ITitleProps {
    arrow: boolean;
    delay: [number, number];
    offset: [number, number];
  }

  // title props
  const titleProps: ITitleProps = {
    arrow: false,
    delay: [500, 0],
    offset: [0, 5],
  };

  // See if singleton exists
  const singletonTarget = typeof singleton === 'string' ? Tooltip.singletons[singleton] : singleton;

  const tooltipContent = useMemo(
    () =>
      shortcutKeys?.length ? (
        <Flex align="center" gap="size-8" justify="start">
          {content}
          <Flex
            align="center"
            gap="size-4"
            justify="start"
            aria-label={`shortcut: ${shortcutKeys.join(' ')}`}
            role="img"
          >
            {shortcutKeys.map((key, index) => (
              <KbdShortcut aria-hidden="true" className="rm-Tooltip-kbd" key={index} kind="outline">
                {key}
              </KbdShortcut>
            ))}
          </Flex>
        </Flex>
      ) : (
        content
      ),
    [content, shortcutKeys],
  );

  if (!content && !shortcutKeys?.length) return children;

  const hideOnEsc = {
    name: 'hideOnEsc',
    defaultValue: true,
    fn({ hide }) {
      function onKeyDown(event) {
        if (event.keyCode === 27) {
          hide();
        }
      }

      return {
        onShow() {
          document.addEventListener('keydown', onKeyDown);
        },
        onHide() {
          document.removeEventListener('keydown', onKeyDown);
        },
      };
    },
  };

  // Accessibility Note: This version of tippy (4.1.0) requires you to pass aria={{ content: 'describedBy' }} for screen
  // readers to read the tooltip content.
  return (
    <Tippy
      className={classes}
      content={tooltipContent}
      duration={150}
      placement="bottom-start"
      plugins={[hideOnEsc]}
      singleton={singletonTarget}
      // This should match var(--z-tooltip) from @core/styles/vars.scss
      zIndex={100000}
      {...(asTitle && titleProps)}
      {...props}
    >
      {trigger as ReactElement}
    </Tippy>
  );
};

/**
 * Hash of singleton target objects by an ID. New entries are created whenever
 * new TooltipSingleton instances are created. Targets are then reference-able
 * based on an ID string.
 */
Tooltip.singletons = {} as Record<string, NonNullable<TippyProps['singleton']>>;

export default Tooltip;
export { default as TooltipSingleton } from './Singleton';
export { useSingleton } from '@tippyjs/react';
